import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordVisibilityToggle } from "@/components/PasswordVisibilityToggle";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLocale } from "@/i18n/context";

/**
 * Completes the password-reset cycle: when Supabase fires PASSWORD_RECOVERY
 * (after the user clicks the reset link), this opens a dialog to set a new
 * password via `auth.updateUser`. Rendered globally so it works on any route.
 */
export function PasswordRecoveryDialog() {
  const { t } = useLocale();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Primary trigger: a `reset=1` marker the reset email's redirect carries.
    // This is robust to the PKCE flow, where the client auto-processes the
    // recovery code (and may fire SIGNED_IN, not PASSWORD_RECOVERY) before any
    // React listener subscribes. The marker survives because detectSessionInUrl
    // only strips the auth params, not our query.
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("reset") === "1") {
        setOpen(true);
        params.delete("reset");
        const query = params.toString();
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
        );
      }
    }

    // Secondary trigger: the live event (e.g. implicit flow).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setOpen(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const validate = (value: string): string => {
    if (value.length < 8) return t.auth.passwordTooShort;
    if (!/(?=.*[a-z])/.test(value)) return t.auth.passwordNeedsLower;
    if (!/(?=.*[A-Z])/.test(value)) return t.auth.passwordNeedsUpper;
    if (!/(?=.*\d)/.test(value)) return t.auth.passwordNeedsDigit;
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const message = validate(password);
    if (message) {
      setError(message);
      return;
    }
    setError("");
    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateError) {
      toast({
        title: t.common.error,
        description: updateError.message,
        variant: "destructive",
      });
      return;
    }
    toast({
      title: t.auth.passwordUpdatedTitle,
      description: t.auth.passwordUpdatedDesc,
    });
    setPassword("");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md border-border/60 shadow-xl">
        <DialogHeader>
          <DialogTitle>{t.auth.recoveryTitle}</DialogTitle>
          <DialogDescription>{t.auth.recoveryDesc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="recovery-password">{t.auth.password}</Label>
            <div className="relative">
              <Input
                id="recovery-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                required
                minLength={8}
                autoComplete="new-password"
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "recovery-password-error" : undefined}
                className={error ? "border-destructive" : ""}
              />
              <PasswordVisibilityToggle
                visible={showPassword}
                onToggle={() => setShowPassword(!showPassword)}
                controls="recovery-password"
              />
            </div>
            {error && (
              <p id="recovery-password-error" role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <p className="text-xs text-muted-foreground">{t.auth.passwordHint}</p>
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t.auth.recoverySubmit}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
